import clsx from "clsx";
import { type ComponentProps, useCallback, useMemo, useState } from "react";

import { ContactMapCard } from "@widgets/contact-map-card";
import { PageHero } from "@widgets/page-hero";

import { RulesSection } from "@features/rules-section/ui";

import { Popup } from "@shared/ui/popup";
import { useScroll } from "@shared/ui/scroll/scroll";

import type { InstructorsPageType } from "../model";
import { FAQSection } from "./faq-section";
import { InfoSection } from "./info";
import { PopupForm } from "./popup-form";
import { PricingPlans } from "./pricing-plans";

import s from "./InstructorsPage.module.scss";

export type InstructorsPageProps = ComponentProps<"div"> & {
  page: InstructorsPageType;
};

export const InstructorsPage = (props: InstructorsPageProps) => {
  const { page } = props;

  const scroll = useScroll();

  const [isOpenPopup, setOpenPopup] = useState(false);

  const handleOpenPopup = useCallback(() => {
    setOpenPopup(true);
  }, []);

  const handlePopupClose = useCallback(() => {
    setOpenPopup(false);
  }, []);

  const handleScrollToPrice = useCallback(() => {
    if (scroll) {
      scroll?.scrollTo("#pricing-plans", { offset: -200 });
    }
  }, [scroll]);

  const heroData = useMemo(() => {
    return {
      subtitle: page.priceText || null,
      buttons: {
        left: {
          ui: page?.heroLeftButton || null,
          onClick: page?.heroLeftButton.link ? () => {} : handleOpenPopup,
          link: page?.heroLeftButton.link || null,
        },
        right: {
          ui: page?.heroRightButton || null,
          onClick: page?.heroRightButton?.link ? () => {} : handleScrollToPrice,
          link: page?.heroRightButton?.link || null,
        },
      },
    };
  }, [page, handleScrollToPrice]);

  return (
    <div className={clsx(s.root)}>
      <PageHero
        variant="primary"
        textColor="brown"
        title={page?.titleWithIcon}
        description={page?.description}
        heroData={heroData}
      />
      {page.info && <InfoSection info={page.info} image={page.mainInfoImage} />}
      {page.pricingPlans && (
        <PricingPlans
          tables={page.pricingPlans}
          moreBtn={heroData?.buttons.left}
        />
      )}
      {page.faq && <FAQSection data={page.faq} />}
      {page.rulesSection && (
        <RulesSection data={page.rulesSection} variant="milk" />
      )}
      {page?.contactMapSection && (
        <div className={clsx(s.mapContainer)}>
          <ContactMapCard
            {...(page.contactMapSection ?? {})}
            onButtonClick={handleOpenPopup}
          />
        </div>
      )}
      <Popup
        id="instructors-popup"
        isOpen={isOpenPopup}
        onClose={handlePopupClose}
        variant="feedback"
      >
        <PopupForm
          title={page?.popup?.title}
          description={page?.popup?.description}
          document={page?.popup?.document}
          isOpen={isOpenPopup}
          onSuccess={handlePopupClose}
        />
      </Popup>
    </div>
  );
};

InstructorsPage.displayName = "InstructorsPage";
